import { z } from 'zod';
import { AssignmentsSchema } from './assignments.js';
import { IdSchema, ListQuerySchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const InspectionStatusSchema = z.enum(['scheduled', 'in_progress', 'completed', 'canceled']);
export const InspectionOutcomeSchema = z.enum([
    'no_action_needed',
    'follow_up_required',
    'work_recommended',
    'emergency_response',
    'other',
]);
/** Structured answers: allowlisted keys, bounded scalar values. */
export const InspectionAnswersSchema = z
    .record(z.string().regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'Answer keys are snake_case'), z.union([z.string().max(1000), z.number(), z.boolean()]))
    .refine((value) => Object.keys(value).length <= 50, { message: 'At most 50 answers' });
export const InspectionSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    jobFileId: IdSchema,
    propertyId: IdSchema.nullable(),
    inspectionTypeId: IdSchema.nullable(),
    status: InspectionStatusSchema,
    scheduledStart: TimestampSchema.nullable(),
    scheduledEnd: TimestampSchema.nullable(),
    startedAt: TimestampSchema.nullable(),
    completedAt: TimestampSchema.nullable(),
    completedByMembershipId: IdSchema.nullable(),
    canceledAt: TimestampSchema.nullable(),
    outcome: InspectionOutcomeSchema.nullable(),
    findings: z.string().nullable(),
    answers: InspectionAnswersSchema,
    followUpAction: z.string().nullable(),
    assignments: AssignmentsSchema,
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
const ScheduleFieldsSchema = z.strictObject({
    scheduledStart: TimestampSchema.nullable().default(null),
    scheduledEnd: TimestampSchema.nullable().default(null),
});
const scheduleInOrder = (value) => !value.scheduledStart || !value.scheduledEnd || value.scheduledStart <= value.scheduledEnd;
const SCHEDULE_ORDER = {
    message: 'scheduledEnd must not be before scheduledStart',
    path: ['scheduledEnd'],
};
export const CreateInspectionRequestSchema = z
    .strictObject({
    jobFileId: IdSchema,
    /** Defaults to the job file's property. */
    propertyId: IdSchema.nullable().default(null),
    inspectionTypeId: IdSchema.nullable().default(null),
    ...ScheduleFieldsSchema.shape,
    owner: IdSchema.nullable().default(null),
    collaborators: z.array(IdSchema).max(20).default([]),
    findings: z.string().trim().max(10_000).nullable().default(null),
})
    .refine(scheduleInOrder, SCHEDULE_ORDER);
export const UpdateInspectionRequestSchema = z
    .strictObject({
    propertyId: IdSchema.nullable().optional(),
    inspectionTypeId: IdSchema.nullable().optional(),
    scheduledStart: TimestampSchema.nullable().optional(),
    scheduledEnd: TimestampSchema.nullable().optional(),
    findings: z.string().trim().max(10_000).nullable().optional(),
    answers: InspectionAnswersSchema.optional(),
    followUpAction: z.string().trim().max(1000).nullable().optional(),
    version: VersionSchema,
})
    .refine(scheduleInOrder, SCHEDULE_ORDER);
export const StartInspectionCommandSchema = z.strictObject({ version: VersionSchema });
/** Completion validates the outcome and answers and stamps the actor and time server-side. */
export const CompleteInspectionCommandSchema = z.strictObject({
    outcome: InspectionOutcomeSchema,
    findings: z.string().trim().max(10_000).nullable().optional(),
    answers: InspectionAnswersSchema.optional(),
    followUpAction: z.string().trim().max(1000).nullable().optional(),
    version: VersionSchema,
});
export const CancelInspectionCommandSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
    version: VersionSchema,
});
export const RescheduleInspectionCommandSchema = z
    .strictObject({
    scheduledStart: TimestampSchema.nullable(),
    scheduledEnd: TimestampSchema.nullable(),
    version: VersionSchema,
})
    .refine(scheduleInOrder, SCHEDULE_ORDER);
export const InspectionListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    propertyId: IdSchema.optional(),
    status: InspectionStatusSchema.optional(),
    assignedMembershipId: IdSchema.optional(),
});
export const InspectionListSchema = z.strictObject({
    items: z.array(InspectionSchema),
    page: PageInfoSchema,
});
/** Checklist items on an inspection type (specification 8.7). */
export const InspectionInputSchema = z.enum(['text', 'number', 'boolean', 'select', 'photo']);
export const InspectionChecklistItemSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    inspectionTypeId: IdSchema,
    key: z.string(),
    label: z.string(),
    input: InspectionInputSchema,
    unit: z.string().nullable(),
    options: z.array(z.string()),
    required: z.boolean(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
const ChecklistKeySchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'Use lowercase letters, digits, and single underscores')
    .max(80);
const ChecklistOptionsSchema = z.array(z.string().trim().min(1).max(120)).max(50);
export const CreateInspectionChecklistItemRequestSchema = z
    .strictObject({
    key: ChecklistKeySchema,
    label: z.string().trim().min(1).max(200),
    input: InspectionInputSchema,
    unit: z.string().trim().max(20).nullable().default(null),
    options: ChecklistOptionsSchema.default([]),
    required: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
})
    .refine((value) => value.input !== 'select' || value.options.length > 0, {
    path: ['options'],
    message: 'A select item needs at least one option',
});
export const UpdateInspectionChecklistItemRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(200).optional(),
    input: InspectionInputSchema.optional(),
    unit: z.string().trim().max(20).nullable().optional(),
    options: ChecklistOptionsSchema.optional(),
    required: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
    version: VersionSchema,
});
export const InspectionChecklistListQuerySchema = z.strictObject({
    includeInactive: z.stringbool().default(false),
});
export const InspectionChecklistListSchema = z.strictObject({
    items: z.array(InspectionChecklistItemSchema),
});
/** What an inspection found in one affected area of the job file. */
export const AreaConditionSchema = z.enum(['dry', 'damp', 'wet', 'saturated', 'unknown']);
export const InspectionAreaFindingSchema = z.strictObject({
    id: IdSchema,
    inspectionId: IdSchema,
    areaId: IdSchema,
    areaName: z.string(),
    condition: AreaConditionSchema,
    moisturePercent: z.number().nullable(),
    materials: z.string().nullable(),
    notes: z.string().nullable(),
    updatedAt: TimestampSchema,
});
export const AreaFindingInputSchema = z.strictObject({
    areaId: IdSchema,
    condition: AreaConditionSchema.default('unknown'),
    moisturePercent: z.number().min(0).max(100).nullable().default(null),
    materials: z.string().trim().max(500).nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
});
/** Replaces the inspection's area findings; each area of the job file appears at most once. */
export const SetAreaFindingsRequestSchema = z
    .strictObject({
    findings: z.array(AreaFindingInputSchema).max(100),
    version: VersionSchema,
})
    .refine((value) => new Set(value.findings.map((finding) => finding.areaId)).size === value.findings.length, { path: ['findings'], message: 'Each area appears once' });
export const InspectionAreaFindingListSchema = z.strictObject({
    items: z.array(InspectionAreaFindingSchema),
});
